import {
  AssessmentStatus,
  CourseEntryLevels,
  DateRangeType,
} from 'models/enum';
import { SM_APP_NAME, SM_SYSTEM_AREA } from 'utilities/constants';
import { IErrorScreen } from './models/IErrorScreen.interface';

export const mutateRequestPayloadProposedSkill = (skills: string[]) => {
  const arrSkillsObj = skills?.map((sk) => {
    return {
      skillName: sk,
    };
  });
  return {
    proposedSkills: [...(arrSkillsObj || {})],
  };
};

export const getAssessmentsStatusOptions = () => {
  return [
    { name: 'Coming soon', value: AssessmentStatus.ComingSoon },
    { name: 'Complete', value: AssessmentStatus.Complete },
    { name: 'Completed', value: AssessmentStatus.Completed },
    { name: 'Deadline passed', value: AssessmentStatus.DeadlinePassed },
    { name: 'Expired', value: AssessmentStatus.Expired },
    { name: 'In progress', value: AssessmentStatus.InProgress },
    {
      name: 'InProgress interrupted',
      value: AssessmentStatus.InProgressInterrupted,
    },
    { name: 'Locked', value: AssessmentStatus.Locked },
    { name: 'New', value: AssessmentStatus.New },
    { name: 'Not invited', value: AssessmentStatus.NotInvited },
    { name: 'Setup is needed', value: AssessmentStatus.NotSettedUp },
    { name: 'Not started', value: AssessmentStatus.NotStarted },
    { name: 'SettedUp', value: AssessmentStatus.SettedUp },
    { name: 'Sufficient', value: AssessmentStatus.Sufficient },
  ];
};

const getCurrentMonthDate = () => {
  const date = new Date();
  const firstDate = new Date(date.getFullYear(), date.getMonth(), 1);
  const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  return {
    firstDate: new Date(firstDate.setHours(0, 0, 0, 0)),
    lastDate: new Date(lastDate.setHours(23, 59, 59, 999)),
  };
};

const getNextMonthDate = () => {
  const date = new Date();
  let firstDate;
  let lastDate;

  if (date.getMonth() === 11) {
    //DECEMBER
    firstDate = new Date(date.getFullYear() + 1, 0, 1);
    lastDate = new Date(date.getFullYear() + 1, 1, 0);
  } else {
    firstDate = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    lastDate = new Date(date.getFullYear(), date.getMonth() + 2, 0);
  }

  return {
    firstDate: new Date(firstDate.setHours(0, 0, 0, 0)),
    lastDate: new Date(lastDate.setHours(23, 59, 59, 999)),
  };
};

//returns the relative day in the week 0 = Sunday, 1 = Monday ... 6 = Saturday
const getRelativeDayInWeek = (
  _day: number,
  type: DateRangeType = DateRangeType.START_DATE
) => {
  const date = new Date();
  const today = date.getDate();
  const currentDay = date.getDay();
  const newDate = date.setDate(today - currentDay + _day);
  const newDateObj =
    type === DateRangeType.START_DATE
      ? new Date(new Date(newDate).setHours(0, 0, 0, 0))
      : new Date(new Date(newDate).setHours(23, 59, 59, 999));

  return newDateObj;
};

export const getAssessSurveyDeadlineOptions = () => {
  const thisWeekSunday = getRelativeDayInWeek(0, DateRangeType.START_DATE);
  const thisWeekSaturday = getRelativeDayInWeek(6, DateRangeType.END_DATE);

  const nextWeekSunday = getRelativeDayInWeek(7, DateRangeType.START_DATE);
  const nextWeekSaturday = getRelativeDayInWeek(13, DateRangeType.END_DATE);

  const currMonthDate = getCurrentMonthDate();
  const nextMonth = getNextMonthDate();

  return [
    {
      name: 'Today',
      startDate: new Date(new Date().setHours(0, 0, 0, 0)),
      endDate: new Date(new Date().setHours(23, 59, 59, 999)),
    },
    { name: 'This week', startDate: thisWeekSunday, endDate: thisWeekSaturday },
    { name: 'Next week', startDate: nextWeekSunday, endDate: nextWeekSaturday },
    {
      name: 'This month',
      startDate: currMonthDate.firstDate,
      endDate: currMonthDate.lastDate,
    },
    {
      name: 'Next month',
      startDate: nextMonth.firstDate,
      endDate: nextMonth.lastDate,
    },
  ];
};

export const isAssessmentsAvailableWithinDeadline = (
  assessDeadline: string,
  deadlineItem: any
) => {
  const deadlineDate = new Date(assessDeadline);
  return (
    deadlineDate >= deadlineItem.startDate &&
    deadlineDate <= deadlineItem.endDate
  );
};

export const getProficiencyLevelOptions = () => {
  return [
    { name: 'Beginner', value: CourseEntryLevels.Beginner },
    { name: 'Intermediate', value: CourseEntryLevels.Intermediate },
    { name: 'Advanced', value: CourseEntryLevels.Advanced },
    { name: 'All Levels', value: CourseEntryLevels.AllLevels },
  ];
};

export const getCourseDurationOptions = (maxDurationsArr: number[]) => {
  return [
    { name: '< 1 hr', minMinutes: 0, maxMinutes: 60 },
    { name: '1 -2 hrs', minMinutes: 60, maxMinutes: 120 },
    {
      name: '> 2 hrs',
      minMinutes: 120,
      maxMinutes: Math.max(...maxDurationsArr) + 1,
    },
  ];
};

export const isCoursesAvailableWithDuration = (
  courseDuration: number,
  duration: any
) => {
  return (
    courseDuration >= duration.minMinutes &&
    courseDuration < duration.maxMinutes
  );
};

export const checkIsSystemMaintenanceRunning = (
  systemMaintenance: IErrorScreen | null
) => {
  if (
    systemMaintenance?.maintenance &&
    systemMaintenance?.maintenance?.application === SM_APP_NAME &&
    systemMaintenance?.maintenance?.systemArea === SM_SYSTEM_AREA
  ) {
    return systemMaintenance?.isSystemMaintenanceRunning;
  } else return false;
};

export const isUnsupportedUserAgent: boolean =
  /webOS|Android|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent) ||
  /webOS|Android|iPhone|iPad|iPod|BlackBerry/i.test(navigator.platform);

export const isApplicationSupportedOnDevice = (
  innerWidth: number = window.innerWidth
): boolean => {
  return !isUnsupportedUserAgent && innerWidth >= 300;
};

const EMAIL_DOMAIN_REGEX = new RegExp('@(.*)');

export const getDomainHint = (email: string) => {
  return EMAIL_DOMAIN_REGEX.exec(email)![1];
};
